import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../utils/firebase';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import BottomTabBar from '../components/BottomTabBar';
import StatsWidget from '../components/StatsWidget';
import { calculateCaloriesBurned } from '../utils/calories';
import { calculateStreak } from '../utils/streaks';
import { toMillis } from '../utils/timestamps';
import { displayActivityName } from '../utils/activities';
import logger from '../utils/logger';
import '../styles/user.css';
import '../styles/buttons.css';
import '../styles/stats.css';

// ---------------------------------------------------------------------------
// Date helpers — all operate in local time to match dateRanges.js / streaks.js
// ---------------------------------------------------------------------------

function startOfYear() {
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1).getTime();
}

// Monday-anchored week start for a given ms timestamp. Returns a Date at
// 00:00 local time on that Monday.
function startOfWeek(ms) {
  const d = new Date(ms);
  const day = d.getDay(); // 0 Sun..6 Sat
  const diff = (day === 0 ? -6 : 1 - day); // shift Sun back to prior Mon
  const anchor = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
  return anchor;
}

function weekLabel(d) {
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// ---------------------------------------------------------------------------
// StatsPage component
// ---------------------------------------------------------------------------

function StatsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [currentWeight, setCurrentWeight] = useState('');
  const [goal, setGoal] = useState({ target: '', type: 'reps' });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgressActivity, setSelectedProgressActivity] = useState('');

  // Auth gate — redirect to login if signed out
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        navigate('/login');
      }
    });
    return () => unsub();
  }, [navigate]);

  // Fetch profile (for currentWeight), goal, and all reps on mount
  useEffect(() => {
    if (!userId) return;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [userDoc, goalDoc, repsSnap] = await Promise.all([
          getDoc(doc(db, 'users', userId)),
          getDoc(doc(db, 'users', userId, 'preferences', 'goals')),
          getDocs(collection(db, 'users', userId, 'reps')),
        ]);

        if (userDoc.exists()) {
          setCurrentWeight(userDoc.data().currentWeight?.toString() || '');
        }
        if (goalDoc.exists()) {
          const g = goalDoc.data();
          setGoal({ target: g.target || '', type: g.type || 'reps' });
        }
        const reps = repsSnap.docs.map(d => ({
          id: d.id,
          ...d.data(),
          activityType: displayActivityName(d.data().activityType),
        }));
        setActivities(reps);
      } catch (err) {
        logger.error(err, { page: 'Stats', action: 'fetchAll' });
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [userId]);

  // -------------------------------------------------------------------------
  // Derived stats (memoized)
  // -------------------------------------------------------------------------

  const totals = useMemo(() => {
    if (!activities.length) {
      return { workouts: 0, calories: 0, topActivity: null };
    }
    let calories = 0;
    const counts = {};
    const workoutDays = new Set();
    for (const a of activities) {
      calories += calculateCaloriesBurned(a, currentWeight);
      counts[a.activityType] = (counts[a.activityType] || 0) + 1;
      const ms = toMillis(a.timestamp);
      if (ms) {
        const d = new Date(ms);
        workoutDays.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      }
    }
    let topActivity = null;
    let topCount = 0;
    for (const [name, n] of Object.entries(counts)) {
      if (n > topCount) {
        topCount = n;
        topActivity = name;
      }
    }
    return { workouts: workoutDays.size, calories: Math.round(calories), topActivity };
  }, [activities, currentWeight]);

  const { currentStreak, longestStreak } = useMemo(
    () => calculateStreak(activities),
    [activities]
  );

  const ytd = useMemo(() => {
    const yearStart = startOfYear();
    let reps = 0, minutes = 0, calories = 0;
    const workoutDays = new Set();
    for (const a of activities) {
      const ms = toMillis(a.timestamp);
      if (ms >= yearStart) {
        reps += a.count || 0;
        minutes += a.time || 0;
        calories += calculateCaloriesBurned(a, currentWeight);
        const d = new Date(ms);
        workoutDays.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      }
    }
    return { workouts: workoutDays.size, reps, minutes, calories: Math.round(calories) };
  }, [activities, currentWeight]);

  // Rolling 12 weeks — workouts per week + calories per week
  const weeklyBuckets = useMemo(() => {
    const now = new Date();
    const todayAnchor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeekStart = startOfWeek(todayAnchor.getTime());
    // Build 12 buckets ending at thisWeekStart
    const buckets = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(thisWeekStart);
      d.setDate(d.getDate() - i * 7);
      buckets.push({
        start: d.getTime(),
        end: d.getTime() + 7 * 24 * 60 * 60 * 1000 - 1,
        label: weekLabel(d),
        workouts: 0,
        calories: 0,
      });
    }
    for (const a of activities) {
      const ms = toMillis(a.timestamp);
      if (!ms) continue;
      for (const b of buckets) {
        if (ms >= b.start && ms <= b.end) {
          b.workouts += 1;
          b.calories += calculateCaloriesBurned(a, currentWeight);
          break;
        }
      }
    }
    return buckets.map(b => ({ ...b, calories: Math.round(b.calories) }));
  }, [activities, currentWeight]);

  // Activity breakdown — % split by type. Any activity with < 3% of total
  // logged sessions is rolled into a single "Other" slice rendered in neutral
  // gray, so the chart stays readable when a user has lots of rare activities.
  const breakdown = useMemo(() => {
    if (!activities.length) return [];
    const counts = {};
    for (const a of activities) {
      counts[a.activityType] = (counts[a.activityType] || 0) + 1;
    }
    const total = activities.length;
    const all = Object.entries(counts)
      .map(([name, value]) => ({
        name,
        value,
        rawPct: total > 0 ? (value / total) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value);

    const main = [];
    let otherValue = 0;
    let otherRawPct = 0;
    for (const item of all) {
      if (item.rawPct < 3) {
        otherValue += item.value;
        otherRawPct += item.rawPct;
      } else {
        main.push({ name: item.name, value: item.value, pct: Math.round(item.rawPct) });
      }
    }
    if (otherValue > 0) {
      main.push({ name: 'Other', value: otherValue, pct: Math.round(otherRawPct), isOther: true });
    }
    return main;
  }, [activities]);

  // Candidate activities for progression chart — must have at least 2 entries
  // with count > 0
  const progressionCandidates = useMemo(() => {
    const perType = {};
    for (const a of activities) {
      if ((a.count || 0) <= 0) continue;
      perType[a.activityType] = (perType[a.activityType] || 0) + 1;
    }
    return Object.keys(perType).filter(t => perType[t] >= 2).sort();
  }, [activities]);

  // Default selected progression activity — first candidate
  useEffect(() => {
    if (!selectedProgressActivity && progressionCandidates.length > 0) {
      setSelectedProgressActivity(progressionCandidates[0]);
    }
  }, [progressionCandidates, selectedProgressActivity]);

  const progressionData = useMemo(() => {
    if (!selectedProgressActivity) return [];
    return activities
      .filter(a => a.activityType === selectedProgressActivity && (a.count || 0) > 0)
      .map(a => ({ ms: toMillis(a.timestamp), count: a.count || 0 }))
      .filter(p => p.ms > 0)
      .sort((a, b) => a.ms - b.ms)
      .map(p => ({
        date: new Date(p.ms).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
        count: p.count,
      }));
  }, [activities, selectedProgressActivity]);

  // Personal bests per activity — only include categories with non-zero values
  const personalBests = useMemo(() => {
    const byType = {};
    for (const a of activities) {
      const t = a.activityType;
      if (!byType[t]) byType[t] = { reps: 0, minutes: 0, weight: 0 };
      if ((a.count || 0) > byType[t].reps) byType[t].reps = a.count || 0;
      if ((a.time || 0) > byType[t].minutes) byType[t].minutes = a.time || 0;
      if ((a.weightLifted || 0) > byType[t].weight) byType[t].weight = a.weightLifted || 0;
    }
    // Filter activities where at least one metric has a non-zero value
    return Object.entries(byType)
      .filter(([, v]) => v.reps > 0 || v.minutes > 0 || v.weight > 0)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [activities]);

  // -------------------------------------------------------------------------
  // Chart colors — reused across all charts. Orange primary + muted palette.
  // -------------------------------------------------------------------------
  const PIE_COLORS = ['#f97316', '#fbbf24', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6', '#ef4444', '#84cc16', '#f59e0b'];
  const axisColor = '#94a3b8';
  const gridColor = '#334155';

  const tooltipStyle = {
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const hasData = activities.length > 0;

  return (
    <div className="App">
      <nav className="nav-desktop">
        <Link to="/user" className="nav-brand">FriendsFitness</Link>
        <div className="nav-links-right">
          <Link to="/user" className={`nav-pill${location.pathname === '/user' ? ' active-link' : ''}`}>User</Link>
          <Link to="/stats" className={`nav-pill${location.pathname === '/stats' ? ' active-link' : ''}`}>Stats</Link>
          <Link to="/community" className={`nav-pill${location.pathname === '/community' ? ' active-link' : ''}`}>Community</Link>
          <Link to="/account" className={`nav-pill${location.pathname === '/account' ? ' active-link' : ''}`}>Account</Link>
          <button className="logout-button" onClick={() => auth.signOut().then(() => navigate('/login'))}>Logout</button>
        </div>
      </nav>
      <BottomTabBar />

      <div className="stats-page">
        <h2 className="stats-page-title">Your Stats</h2>

        {loading ? (
          <p className="loading">Loading...</p>
        ) : !hasData ? (
          <div className="weight-tracking">
            <div className="empty-state">
              <p className="empty-state-headline">No workouts yet</p>
              <p className="empty-state-subtitle">Log some activities to see your stats here.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="stats-summary-grid">
              <div className="stats-summary-card">
                <div className="stats-summary-label">Total Workouts</div>
                <div className="stats-summary-value">{totals.workouts}</div>
              </div>
              <div className="stats-summary-card">
                <div className="stats-summary-label">Total Calories Burned</div>
                <div className="stats-summary-value">{totals.calories.toLocaleString()}</div>
              </div>
              <div className="stats-summary-card">
                <div className="stats-summary-label">Streak</div>
                <div className="stats-summary-value">{currentStreak}</div>
                <div className="stats-summary-sub">Best: {longestStreak}</div>
              </div>
              <div className="stats-summary-card">
                <div className="stats-summary-label">Most Logged</div>
                <div className="stats-summary-value stats-summary-value--sm">{totals.topActivity || '—'}</div>
              </div>
            </div>

            {/* YTD stats */}
            <div className="weight-tracking">
              <h3>Year to Date</h3>
              <div className="stats-ytd-grid">
                <div className="stats-ytd-item">
                  <div className="stats-ytd-label">Workouts</div>
                  <div className="stats-ytd-value">{ytd.workouts}</div>
                </div>
                <div className="stats-ytd-item">
                  <div className="stats-ytd-label">Reps</div>
                  <div className="stats-ytd-value">{ytd.reps.toLocaleString()}</div>
                </div>
                <div className="stats-ytd-item">
                  <div className="stats-ytd-label">Minutes</div>
                  <div className="stats-ytd-value">{ytd.minutes.toLocaleString()}</div>
                </div>
                <div className="stats-ytd-item">
                  <div className="stats-ytd-label">Calories</div>
                  <div className="stats-ytd-value">{ytd.calories.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Workouts per week */}
            <div className="weight-tracking">
              <h3>Workouts per week (last 12 weeks)</h3>
              <div className="stats-chart-wrap">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={weeklyBuckets}>
                    <CartesianGrid stroke={gridColor} vertical={false} />
                    <XAxis dataKey="label" stroke={axisColor} tick={{ fontSize: 11 }} />
                    <YAxis stroke={axisColor} tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(249, 115, 22, 0.1)' }} />
                    <Bar dataKey="workouts" fill="#f97316" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Calories per week */}
            <div className="weight-tracking">
              <h3>Calories burned per week (last 12 weeks)</h3>
              <div className="stats-chart-wrap">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={weeklyBuckets}>
                    <CartesianGrid stroke={gridColor} vertical={false} />
                    <XAxis dataKey="label" stroke={axisColor} tick={{ fontSize: 11 }} />
                    <YAxis stroke={axisColor} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(249, 115, 22, 0.1)' }} />
                    <Bar dataKey="calories" fill="#f97316" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Activity breakdown */}
            <div className="weight-tracking">
              <h3>Activity breakdown</h3>
              {breakdown.length === 0 ? (
                <p className="empty-state-subtitle">No activities to break down yet.</p>
              ) : (
                <div className="stats-chart-wrap">
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={breakdown}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        label={(entry) => `${entry.pct}%`}
                        labelLine={false}
                      >
                        {breakdown.map((entry, i) => (
                          <Cell key={i} fill={entry.isOther ? '#6b7280' : PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ color: '#f1f5f9', fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Per-activity progression */}
            <div className="weight-tracking">
              <h3>Progression by activity</h3>
              {progressionCandidates.length === 0 ? (
                <p className="empty-state-subtitle">Need at least 2 rep-based sessions of the same activity to see progression.</p>
              ) : (
                <>
                  <div className="stats-progression-select">
                    <label htmlFor="progression-activity">Activity:</label>
                    <select
                      id="progression-activity"
                      value={selectedProgressActivity}
                      onChange={(e) => setSelectedProgressActivity(e.target.value)}
                    >
                      {progressionCandidates.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="stats-chart-wrap">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={progressionData}>
                        <CartesianGrid stroke={gridColor} vertical={false} />
                        <XAxis dataKey="date" stroke={axisColor} tick={{ fontSize: 11 }} />
                        <YAxis stroke={axisColor} tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#f97316"
                          strokeWidth={2}
                          dot={{ fill: '#f97316', r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>

            {/* Personal bests */}
            <div className="weight-tracking">
              <h3>Personal Bests</h3>
              {personalBests.length === 0 ? (
                <p className="empty-state-subtitle">Log some activities with reps, minutes, or weights to see personal bests.</p>
              ) : (
                <div className="stats-pb-grid">
                  {personalBests.map(pb => (
                    <div key={pb.name} className="stats-pb-card">
                      <div className="stats-pb-name">{pb.name}</div>
                      <div className="stats-pb-metrics">
                        {pb.reps > 0 && (
                          <div className="stats-pb-metric">
                            <span className="stats-pb-metric-value">{pb.reps}</span>
                            <span className="stats-pb-metric-label">reps</span>
                          </div>
                        )}
                        {pb.minutes > 0 && (
                          <div className="stats-pb-metric">
                            <span className="stats-pb-metric-value">{pb.minutes}</span>
                            <span className="stats-pb-metric-label">min</span>
                          </div>
                        )}
                        {pb.weight > 0 && (
                          <div className="stats-pb-metric">
                            <span className="stats-pb-metric-value">{pb.weight}</span>
                            <span className="stats-pb-metric-label">lbs</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Existing stats widget */}
            <StatsWidget activities={activities} currentWeight={currentWeight} goal={goal} />
          </>
        )}
      </div>
    </div>
  );
}

export default StatsPage;
